import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Eye,
  EyeOff,
  PartyPopper,
  Trophy,
  RotateCcw,
  Loader2,
  CalendarDays,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { getPillarById } from "@/data/pillars";

function GoalRow({ goal, color, onChange, onDelete }) {
  const update = async (changes) => {
    try {
      const { data } = await api.put(`/goals/${goal.id}`, changes);
      onChange(data);
      if (changes.achieved === true) toast.success("Goal achieved. Nicely done! 🎉");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveText = async (text) => {
    if (text === goal.text) return;
    update({ text });
  };
  const saveDate = async (target_date) => {
    if ((target_date || null) === (goal.target_date || null)) return;
    update({ target_date });
  };

  if (goal.achieved) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-xl p-4 border flex items-center gap-3"
        style={{ backgroundColor: `${color}22`, borderColor: `${color}66` }}
        data-testid={`goal-row-${goal.id}`}
      >
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: color }}
        >
          <Trophy className="w-4.5 h-4.5 text-foreground/80" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: color, color: "#2C2C2C" }}
            >
              Achieved
            </span>
            {goal.target_date && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {fmtDate(goal.target_date)}
              </span>
            )}
          </div>
          <p className="text-foreground font-medium leading-snug truncate">{goal.text}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => update({ achieved: false })}
            title="Reopen goal"
            data-testid={`goal-reopen-${goal.id}`}
            className="p-2 rounded-md text-foreground/60 hover:text-foreground hover:bg-white/50 transition-colors duration-200"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => update({ visible: !goal.visible })}
            title={goal.visible ? "Hide on reference page" : "Show on reference page"}
            data-testid={`goal-visibility-${goal.id}`}
            className="p-2 rounded-md text-foreground/60 hover:text-foreground hover:bg-white/50 transition-colors duration-200"
          >
            {goal.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete(goal.id)}
            title="Delete goal"
            data-testid={`goal-delete-${goal.id}`}
            className="p-2 rounded-md text-foreground/60 hover:text-destructive hover:bg-white/50 transition-colors duration-200"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      className={`rounded-xl p-3 border border-border bg-card flex items-center gap-3 transition-opacity duration-200 ${
        goal.visible ? "" : "opacity-60"
      }`}
      data-testid={`goal-row-${goal.id}`}
    >
      <button
        onClick={() => update({ achieved: true })}
        title="Mark as achieved"
        data-testid={`goal-achieve-${goal.id}`}
        className="w-6 h-6 rounded-full border-2 shrink-0 hover:scale-110 transition-transform duration-200"
        style={{ borderColor: color }}
      />
      <input
        defaultValue={goal.text}
        onBlur={(e) => saveText(e.target.value)}
        data-testid={`goal-text-${goal.id}`}
        className="flex-1 min-w-0 bg-transparent border-none focus:outline-none text-foreground/90"
      />
      <input
        type="date"
        defaultValue={goal.target_date || ""}
        onChange={(e) => saveDate(e.target.value)}
        className="w-[150px] px-2 py-1.5 rounded-md border border-input bg-white/70 text-sm text-muted-foreground focus:outline-none focus:border-foreground/40 shrink-0"
      />
      <button
        onClick={() => update({ visible: !goal.visible })}
        title={goal.visible ? "Hide on reference page" : "Show on reference page"}
        data-testid={`goal-visibility-${goal.id}`}
        className="p-2 rounded-md text-foreground/50 hover:text-foreground hover:bg-secondary transition-colors duration-200 shrink-0"
      >
        {goal.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      <button
        onClick={() => onDelete(goal.id)}
        title="Delete goal"
        data-testid={`goal-delete-${goal.id}`}
        className="p-2 rounded-md text-foreground/50 hover:text-destructive hover:bg-secondary transition-colors duration-200 shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

export default function GoalsPage() {
  const { pillarId } = useParams();
  const pillar = getPillarById(pillarId);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [newDate, setNewDate] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!pillar) return;
    setLoading(true);
    api
      .get(`/pillars/${pillar.name}/goals`)
      .then(({ data }) => setGoals(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [pillar]);

  if (!pillar) {
    return (
      <div className="px-8 py-16">
        <p className="text-muted-foreground">Pillar not found.</p>
      </div>
    );
  }

  const addGoal = async (e) => {
    e.preventDefault();
    if (!newText.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/pillars/${pillar.name}/goals`, {
        text: newText.trim(),
        target_date: newDate || null,
      });
      setGoals((g) => [...g, data]);
      setNewText("");
      setNewDate("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const onChange = (updated) =>
    setGoals((g) => g.map((x) => (x.id === updated.id ? updated : x)));

  const onDelete = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      setGoals((g) => g.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const active = goals.filter((g) => !g.achieved);
  const achieved = goals.filter((g) => g.achieved);

  return (
    <div className="max-w-3xl px-6 md:px-12 py-10 md:py-14" data-testid={`goals-page-${pillar.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: pillar.color }} />
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {pillar.name} · Goals
        </span>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <h1 className="font-serif text-3xl md:text-4xl text-foreground">Manage goals</h1>
        <Link
          to={`/pillar/${pillar.id}`}
          className="text-sm text-foreground/70 hover:text-foreground flex items-center gap-1.5"
          data-testid="goals-view-reference"
        >
          <BookOpen className="w-4 h-4" /> View summary
        </Link>
      </div>

      {/* Add goal */}
      <form onSubmit={addGoal} className="flex items-center gap-2 mb-8" data-testid="goal-add-form">
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a new goal…"
          data-testid="goal-new-text"
          className="flex-1 px-3.5 py-2.5 rounded-md border border-input bg-white/70 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200"
        />
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          data-testid="goal-new-date"
          className="w-[150px] px-2 py-2.5 rounded-md border border-input bg-white/70 text-sm text-muted-foreground focus:outline-none focus:border-foreground/40"
        />
        <button
          type="submit"
          disabled={adding || !newText.trim()}
          data-testid="goal-add-submit"
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-md text-background font-medium disabled:opacity-50 transition-opacity duration-200"
          style={{ backgroundColor: pillar.color, color: "#2C2C2C" }}
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {goals.length === 0 && (
            <p className="text-muted-foreground italic" data-testid="goals-empty">
              No goals yet. Add your first one above.
            </p>
          )}

          {active.length > 0 && (
            <div className="space-y-2 mb-10">
              <AnimatePresence>
                {active.map((goal) => (
                  <GoalRow
                    key={goal.id}
                    goal={goal}
                    color={pillar.color}
                    onChange={onChange}
                    onDelete={onDelete}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {achieved.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5 mb-3">
                <PartyPopper className="w-3.5 h-3.5" /> Achieved · {achieved.length}
              </p>
              <div className="space-y-2.5">
                <AnimatePresence>
                  {achieved.map((goal) => (
                    <GoalRow
                      key={goal.id}
                      goal={goal}
                      color={pillar.color}
                      onChange={onChange}
                      onDelete={onDelete}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
